import { useState, useEffect, useMemo } from 'react';
import type { BroUser } from '@/components/MiniApp/Jobs/types';

export interface JobBoardAccess {
  hasValidBroCode: boolean;
  hasTwitterHandle: boolean;
  hasMinimumScore: boolean;
  isVerified: boolean;
  canPostJobs: boolean;
  canApplyToJobs: boolean;
  canBrowseJobs: boolean;
  missingRequirements: string[];
  verificationLevel: 'none' | 'browse' | 'apply' | 'post' | 'full';
}

interface AccessRequirements {
  browse: {
    minBroScore: 0;
    requiresBroCode: false;
    requiresTwitter: false;
    requiresVerification: false;
  };
  apply: {
    minBroScore: number;
    requiresBroCode: boolean;
    requiresTwitter: boolean;
    requiresVerification: boolean;
  };
  post: {
    minBroScore: number;
    requiresBroCode: boolean;
    requiresTwitter: boolean;
    requiresVerification: boolean;
    requiresEscrowBalance: boolean;
  };
}

const DEFAULT_REQUIREMENTS: AccessRequirements = {
  browse: {
    minBroScore: 0,
    requiresBroCode: false,
    requiresTwitter: false,
    requiresVerification: false,
  },
  apply: {
    minBroScore: 25,
    requiresBroCode: true,
    requiresTwitter: true,
    requiresVerification: true,
  },
  post: {
    minBroScore: 100,
    requiresBroCode: true,
    requiresTwitter: true,
    requiresVerification: true,
    requiresEscrowBalance: true,
  },
};

export const useJobBoardAccess = (user: BroUser | null) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<AccessRequirements>(DEFAULT_REQUIREMENTS);
  const [escrowBalance, setEscrowBalance] = useState<number>(0);

  // Fetch current access requirements and user balance
  useEffect(() => {
    const fetchAccessRequirements = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use default requirements since job_board_config table doesn't exist yet
        // In a future implementation, this could be fetched from a config table
        setRequirements(DEFAULT_REQUIREMENTS);

        // For now, use user's BroScore as escrow balance
        // In a future implementation, this would check actual escrow balance
        if (user?.bro_score) {
          setEscrowBalance(user.bro_score);
        }
      } catch (err) {
        console.error('Error fetching job board access requirements:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch access requirements');
      } finally {
        setLoading(false);
      }
    };

    fetchAccessRequirements();
  }, [user?.id]);

  // Calculate access levels and verification status
  const access = useMemo((): JobBoardAccess => {
    if (!user) {
      return {
        hasValidBroCode: false,
        hasTwitterHandle: false,
        hasMinimumScore: false,
        isVerified: false,
        canPostJobs: false,
        canApplyToJobs: false,
        canBrowseJobs: true, // Allow browsing without authentication
        missingRequirements: ['Valid BroCode', 'Twitter Handle Verification', 'Minimum BroScore'],
        verificationLevel: 'browse'
      };
    }

    // Check individual verification requirements
    const hasValidBroCode = Boolean(user.brocode && user.brocode.length > 0);
    const hasTwitterHandle = Boolean(user.twitter_handle && user.twitter_handle.length > 0);
    const isVerified = Boolean(user.telegram_verified);
    const broScore = user.bro_score || 0;

    // Check minimum scores for different access levels
    const hasApplyMinScore = broScore >= requirements.apply.minBroScore;
    const hasPostMinScore = broScore >= requirements.post.minBroScore;
    const hasEscrowBalance = escrowBalance >= 50; // Minimum escrow for job posting

    // Calculate what requirements are missing
    const missingRequirements: string[] = [];

    // Apply requirements check - simplified for MiniApp
    if (!hasApplyMinScore) {
      missingRequirements.push(`Minimum ${requirements.apply.minBroScore} BroScore (Current: ${broScore})`);
    }

    // Post requirements check (additional to apply requirements)
    if (!hasPostMinScore) {
      missingRequirements.push(`Minimum ${requirements.post.minBroScore} BroScore for posting`);
    }
    if (requirements.post.requiresEscrowBalance && !hasEscrowBalance) {
      missingRequirements.push(`Minimum 50 BroScore escrow balance (Current: ${escrowBalance})`);
    }

    // Determine access levels
    const canBrowseJobs = true; // Everyone can browse
    
    // In MiniApp context, users are already verified by being authenticated
    const canApplyToJobs = hasApplyMinScore;

    const canPostJobs = 
      canApplyToJobs &&
      hasPostMinScore &&
      (!requirements.post.requiresEscrowBalance || hasEscrowBalance);

    // Determine verification level
    let verificationLevel: JobBoardAccess['verificationLevel'] = 'none';
    if (canPostJobs) {
      verificationLevel = 'full';
    } else if (canApplyToJobs) {
      verificationLevel = 'apply';
    } else if (canBrowseJobs) {
      verificationLevel = 'browse';
    }

    return {
      hasValidBroCode,
      hasTwitterHandle,
      hasMinimumScore: hasApplyMinScore,
      isVerified,
      canPostJobs,
      canApplyToJobs,
      canBrowseJobs,
      missingRequirements: [...new Set(missingRequirements)], // Remove duplicates
      verificationLevel
    };
  }, [user, requirements, escrowBalance]);

  // Methods to help complete verification
  const getVerificationSteps = () => {
    const steps = [];
    
    if (!access.hasValidBroCode) {
      steps.push({
        id: 'brocode',
        title: 'Link BroCode',
        description: 'Connect your BroCode to access the job board',
        action: 'link-brocode',
        completed: false
      });
    }

    if (!access.hasTwitterHandle) {
      steps.push({
        id: 'twitter',
        title: 'Verify Twitter Handle',
        description: 'Link and verify your Twitter account',
        action: 'verify-twitter',
        completed: false
      });
    }

    if (!access.isVerified) {
      steps.push({
        id: 'telegram',
        title: 'Verify Telegram',
        description: 'Complete Telegram verification process',
        action: 'verify-telegram',
        completed: false
      });
    }

    if (!access.hasMinimumScore) {
      steps.push({
        id: 'broscore',
        title: 'Earn BroScore',
        description: `Reach minimum ${requirements.apply.minBroScore} BroScore through engagement`,
        action: 'earn-broscore',
        completed: false
      });
    }

    if (access.canApplyToJobs && !access.canPostJobs && escrowBalance < 50) {
      steps.push({
        id: 'escrow',
        title: 'Fund Escrow',
        description: 'Add funds to escrow account for job posting',
        action: 'fund-escrow',
        completed: false
      });
    }

    return steps;
  };

  const refreshAccess = async () => {
    setLoading(true);
    // Re-fetch user data and requirements
    window.location.reload(); // Simple refresh for now
  };

  return {
    access,
    loading,
    error,
    requirements,
    escrowBalance,
    getVerificationSteps,
    refreshAccess
  };
};

export default useJobBoardAccess;